import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getDockById } from '../../services/business/dockService';
import { getDailyReservations } from '../../services/business/reservationService';
import { getGeneralSettings } from '../../services/firestore/systemConfigService';
import { getVehicleMovementLogsByDateRange } from '../../services/logging/vehicleMovementLogger';
import { VEHICLE_MOVEMENT_EVENTS } from '../../constants/systemConfig';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import '../../styles/components/dockScreen.css';
import SegmentedProgressBar from '../../components/shared/SegmentedProgressBar';

dayjs.locale('tr');

// Status Colors
const STATUS_COLORS = {
    FREE: '#22c55e',      // Green - Available
    OCCUPIED: '#f59e0b',  // Orange - Vehicle docked
    LOADING: '#3b82f6',   // Blue - Loading in progress
    MAINTENANCE: '#ef4444' // Red - Maintenance
};

const STATUS_LABELS = {
    FREE: 'MÜSAİT',
    OCCUPIED: 'ARAÇ YANAŞTI',
    LOADING: 'YÜKLEME YAPILIYOR',
    MAINTENANCE: 'BAKIMDA'
};



const WAITING_STATUSES = ['pending', 'pending_approval', 'approved', 'confirmed', 'facility_entered', 'in_parking', 'checked_in'];

// Digital Clock Component
const DigitalClock = () => {
    const [time, setTime] = useState(dayjs());

    useEffect(() => {
        const timer = setInterval(() => setTime(dayjs()), 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="digital-clock">
            <div className="clock-date">
                {time.format('DD MMMM YYYY dddd')}
            </div>
            <div className="clock-time">
                {time.format('HH:mm:ss')}
            </div>
        </div>
    );
};

// Status Indicator - Large visible from distance
const StatusIndicator = ({ status }) => {
    const color = STATUS_COLORS[status] || STATUS_COLORS.FREE;
    const label = STATUS_LABELS[status] || 'BİLİNMİYOR';

    return (
        <div className="status-indicator-large" style={{ '--status-color': color }}>
            <div className="status-dot" />
            <span className="status-label">{label}</span>
        </div>
    );
};

// Metric Donut Chart - CSS Conic Gradient
const MetricDonut = ({ percentage, label, subLabel, color, tracks }) => {
    // Determine gradient string
    let backgroundStr = '';

    if (tracks && tracks.length > 0) {
        // Multi-segment pie (e.g. for Vehicles)
        // tracks: [{ percent: 20, color: 'blue' }, { percent: 30, color: 'red' }]
        let currentDeg = 0;
        const segments = tracks.map(t => {
            const start = currentDeg;
            const end = currentDeg + (t.percent * 3.6);
            currentDeg = end;
            return `${t.color} ${start}deg ${end}deg`;
        });
        // Fill remaining with track color
        if (currentDeg < 360) {
            segments.push(`rgba(255,255,255,0.1) ${currentDeg}deg 360deg`);
        }
        backgroundStr = `conic-gradient(${segments.join(', ')})`;
    } else {
        // Simple single percentage
        backgroundStr = `conic-gradient(${color} 0% ${percentage}%, rgba(255,255,255,0.1) ${percentage}% 100%)`;
    }

    return (
        <div className="metric-donut-card" style={{ '--card-glow': color }}>
            <div className="donut-chart" style={{ background: backgroundStr }}>
                <div className="donut-hole">
                    <span className="donut-value" style={{ color: color }}>
                        {tracks ? tracks.reduce((acc, t) => acc + t.value, 0) : `${Math.round(percentage)}%`}
                    </span>
                </div>
            </div>
            <div className="donut-info">
                <span className="donut-label">{label}</span>
                <span className="donut-sublabel">{subLabel}</span>
            </div>
        </div>
    );
};

// Metrics Grid
const DockMetricsGrid = ({ occupancy, metrics }) => {
    // Volume %
    const volPercent = metrics.totalVolume > 0 ? (metrics.completedVolume / metrics.totalVolume) * 100 : 0;

    // Weight %
    const weightPercent = metrics.totalWeight > 0 ? (metrics.completedWeight / metrics.totalWeight) * 100 : 0;

    // Vehicle Segments
    const totalVehicles = metrics.totalVehicles || 1; // avoid div 0
    // Segments: Completed (Green), NoShow (Red), Active/Pending (Blue/Orange -> let's use primary)
    // We want to show distribution.
    const vehicleTracks = [
        { percent: (metrics.completedVehicles / totalVehicles) * 100, color: '#22c55e', value: metrics.completedVehicles }, // Completed
        { percent: (metrics.noShowVehicles / totalVehicles) * 100, color: '#ef4444', value: metrics.noShowVehicles }, // No Show
        { percent: (metrics.activeVehicles / totalVehicles) * 100, color: '#3b82f6', value: metrics.activeVehicles } // Active/Pending
    ];

    return (
        <div className="metrics-grid">
            {/* 1. Occupancy */}
            <MetricDonut
                percentage={occupancy}
                label="GÜNLÜK YOĞUNLUK"
                subLabel="Kapasite Kullanımı"
                color={occupancy > 90 ? '#ef4444' : occupancy > 70 ? '#f59e0b' : '#22c55e'}
            />

            {/* 2. Volume */}
            <MetricDonut
                percentage={volPercent}
                label="HACİM (m³)"
                subLabel={`${metrics.completedVolume} / ${metrics.totalVolume} m³`}
                color="#8b5cf6"
            />

            {/* 3. Weight */}
            <MetricDonut
                percentage={weightPercent}
                label="AĞIRLIK (ton)"
                subLabel={`${metrics.completedWeight} / ${metrics.totalWeight} ton`}
                color="#ec4899"
            />

            {/* 4. Vehicles */}
            <MetricDonut
                tracks={vehicleTracks}
                label="ARAÇ DURUMU"
                subLabel={`${metrics.completedVehicles} Tamam / ${metrics.noShowVehicles} Gelmedi`}
                color="#ffffff"
            />
        </div>
    );
};

// Current Vehicle Card - Large
const CurrentVehicleCard = ({ reservation }) => {
    if (!reservation) {
        return (
            <div className="current-vehicle-card empty">
                <div className="empty-icon">🚪</div>
                <div className="empty-text">Rampada araç yok</div>
                <div className="empty-subtext">Sıradaki araç bekliyor...</div>
            </div>
        );
    }

    const startTime = dayjs(reservation.startTime);
    const endTime = dayjs(reservation.endTime);
    // Round up to nearest 20 mins slot duration
    const diffMins = endTime.diff(startTime, 'minute');
    const duration = diffMins > 0 ? Math.round(diffMins) : 0;

    return (
        <div className="current-vehicle-card active">
            <div className="vehicle-header-large">
                <span className="vehicle-plate-large">{reservation.plateNumber || 'PLAKA GİRİLMEDİ'}</span>
            </div>
            <div className="vehicle-details">
                <div className="detail-row">
                    <span className="detail-label">Nakliyeci</span>
                    <span className="detail-value">{reservation.transporterName || '-'}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Sürücü</span>
                    <span className="detail-value">{reservation.driverName || '-'}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">İletişim</span>
                    <span className="detail-value">{reservation.driverPhone || '-'}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Zaman</span>
                    <span className="detail-value highlight" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {startTime.format('HH:mm')} - {endTime.format('HH:mm')}
                        {dayjs().isAfter(endTime) && (
                            <span style={{
                                fontSize: '0.7rem',
                                padding: '2px 6px',
                                background: 'rgba(239, 68, 68, 0.2)',
                                border: '1px solid #ef4444',
                                color: '#ef4444',
                                borderRadius: '4px',
                                animation: 'pulse 2s infinite',
                                fontWeight: 'bold',
                                letterSpacing: '0.5px'
                            }}>
                                ⚠ GECİKTİ
                            </span>
                        )}
                    </span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Rampa Giriş</span>
                    <span className="detail-value" style={{ color: '#fff' }}>
                        {(() => {
                            if (!reservation.actualStartTime) return '--:--';
                            const d = dayjs(reservation.actualStartTime);
                            return d.isValid() ? d.format('HH:mm') : '--:--';
                        })()}
                    </span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Süre</span>
                    <span className="detail-value">{duration} dk</span>
                </div>

                {/* Progress Bar for Current Vehicle */}
                <div style={{ marginTop: '1.5rem', width: '100%' }}>
                    {(() => {
                        const now = dayjs();
                        const start = dayjs(reservation.actualStartTime || reservation.startTime);
                        const end = start.add(duration, 'minute');

                        const totalMins = Math.max(duration, 1);
                        const elapsed = Math.max(0, now.diff(start, 'minute'));
                        const remaining = Math.max(0, totalMins - elapsed);
                        // Progress pct (0 to 100)
                        const pct = Math.min((elapsed / totalMins) * 100, 100);

                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#9ca3af', fontWeight: '500' }}>
                                    <span>{duration} DK Planlanan</span>
                                    <span>{elapsed} DK Geçen</span>
                                </div>
                                <SegmentedProgressBar
                                    value={pct}
                                    label="YÜKLEME SÜRECİ"
                                    reverse={true}
                                    color={remaining > 15 ? '#22c55e' : remaining > 0 ? '#f59e0b' : '#ef4444'}
                                />
                                <div style={{ textAlign: 'right', marginTop: '5px', fontSize: '1.2rem', fontWeight: 'bold', color: remaining === 0 ? '#ef4444' : '#22c55e' }}>
                                    Kalan: {remaining} DK
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Helper variables for view */}
                {(() => {
                    const vol = reservation.metrics?.volume || reservation.plannedVolume;
                    const weight = reservation.metrics?.weight || reservation.plannedWeight;

                    if (!vol && !weight) return null;

                    return (
                        <div className="detail-row">
                            <span className="detail-label">Yük</span>
                            <span className="detail-value">
                                {vol ? `${vol} m³` : ''}
                                {vol && weight ? ' / ' : ''}
                                {weight ? `${weight} ton` : ''}
                            </span>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

// Queue VehicleCard - Compact
const QueueVehicleCard = ({ reservation, index, isHistory }) => {
    const startTime = dayjs(reservation.startTime);
    const endTime = dayjs(reservation.endTime);
    const now = dayjs();

    // Logic for status
    let statusClass = 'pending';
    let statusLabel = '';



    if (reservation.status === 'loading_completed') {
        statusClass = 'completed';
        statusLabel = 'TAMAMLANDI';
    } else if (isHistory && WAITING_STATUSES.includes(reservation.status)) {
        // In History list & pending = Did Not Arrive (No Show)
        statusClass = 'noshow';
        statusLabel = 'GELMEDİ';
    } else if (!isHistory && now.isAfter(startTime) && WAITING_STATUSES.includes(reservation.status)) {
        // In Upcoming list & pending & time passed = Delayed (Late)
        statusClass = 'delayed';
        statusLabel = 'GECİKTİ';
    } else if (['dock_arrival', 'loading_started'].includes(reservation.status)) {
        statusClass = 'active';
        statusLabel = 'İŞLEMDE';
    }

    // Styles based on class
    const getStyle = () => {
        switch (statusClass) {
            case 'completed': return { borderLeft: '3px solid #22c55e', opacity: 0.8 }; // Green
            case 'noshow': return { borderLeft: '3px solid #ef4444', opacity: 0.6, background: 'rgba(239, 68, 68, 0.05)' }; // Red Border + Dimmed
            case 'delayed': return { borderLeft: '3px solid #f97316', background: 'rgba(249, 115, 22, 0.1)' }; // Orange Border
            case 'active': return { borderLeft: '3px solid #3b82f6', background: 'rgba(59, 130, 246, 0.1)' }; // Blue
            default: return { borderLeft: '3px solid transparent' };
        }
    };

    return (
        <div className="queue-vehicle-card" style={getStyle()}>
            <div className="queue-index" style={{ color: statusClass === 'noshow' ? '#ef4444' : 'inherit' }}>{index + 1}</div>
            <div className="queue-info">
                <div className="flex justify-between items-center">
                    <div className="queue-plate" style={{
                        textDecoration: statusClass === 'noshow' ? 'line-through' : 'none',
                        color: statusClass === 'delayed' ? '#f97316' : statusClass === 'noshow' ? '#ef4444' : 'inherit'
                    }}>
                        {reservation.plateNumber || 'PLAKA BELİRSİZ'}
                    </div>
                    {statusLabel && (
                        <span className={`badge`}
                            style={{
                                fontSize: '9px', padding: '2px 4px', height: 'auto',
                                background: statusClass === 'delayed' ? '#f97316' : statusClass === 'noshow' ? '#ef4444' : undefined,
                                color: '#fff'
                            }}>
                            {statusLabel}
                        </span>
                    )}
                </div>
                <div className="queue-details">
                    <span>{reservation.transporterName || '-'}</span>
                    <span className="queue-time">{startTime.format('HH:mm')} - {endTime.format('HH:mm')}</span>
                </div>
            </div>
        </div>
    );
};

const DockStatusScreen = ({ userData }) => {
    const { dockId } = useParams();
    const [searchParams] = useSearchParams();
    const tenantId = searchParams.get('tenant') || userData?.tenantId;

    const [loading, setLoading] = useState(true);
    const [dock, setDock] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [currentVehicle, setCurrentVehicle] = useState(null);
    const [occupancyPercent, setOccupancyPercent] = useState(0);
    const [dockStatus, setDockStatus] = useState('FREE');

    // New Metrics State
    const [dailyMetrics, setDailyMetrics] = useState({
        totalVolume: 0, completedVolume: 0,
        totalWeight: 0, completedWeight: 0,
        totalVehicles: 0, completedVehicles: 0, noShowVehicles: 0, activeVehicles: 0
    });

    const loadData = useCallback(async () => {
        if (!tenantId || !dockId) return;

        try {
            const todayStr = dayjs().format('YYYY-MM-DD');
            const startOfDay = dayjs().startOf('day').toDate();
            const endOfDay = dayjs().endOf('day').toDate();

            const [dockData, resData, params, logs] = await Promise.all([
                getDockById(dockId, tenantId),
                getDailyReservations(tenantId, todayStr),
                getGeneralSettings(tenantId),
                getVehicleMovementLogsByDateRange(tenantId, startOfDay, endOfDay)
            ]);

            setDock(dockData);

            // Check dock maintenance status
            if (dockData?.status === 'maintenance') {
                setDockStatus('MAINTENANCE');
            }

            // Filter reservations for this dock
            const dockReservations = resData
                .filter(r => r.dockId === dockId && r.status !== 'cancelled')
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

            setReservations(dockReservations);

            // Find current active vehicle and determine status
            const now = dayjs();
            let status = 'FREE';
            let activeRes = null;

            // Metrics Calculation
            let vTotal = 0, vComp = 0;
            let wTotal = 0, wComp = 0;
            let countTotal = dockReservations.length;
            let countComp = 0, countNoShow = 0, countActive = 0;

            for (const r of dockReservations) {
                // Volume & Weight
                const vol = Number(r.metrics?.volume || r.plannedVolume) || 0;
                const weight = Number(r.metrics?.weight || r.plannedWeight) || 0;

                vTotal += vol;
                wTotal += weight;

                if (r.status === 'completed' || r.status === 'loading_completed') {
                    vComp += vol;
                    wComp += weight;
                    countComp++;
                } else if (r.status === 'pending' && now.isAfter(dayjs(r.endTime))) {
                    countNoShow++;
                } else {
                    countActive++;
                }

                // Active Status Check
                if (!activeRes) {
                    // 1. Priority: Direct Status Check from Reservation (The most reliable source)
                    if (r.status === 'loading') {
                        status = 'LOADING';
                        activeRes = r;
                    } else if (r.status === 'arrived') {
                        status = 'OCCUPIED';
                        activeRes = r;
                    }

                    // 2. Secondary: Logs (if status logic wasn't enough)
                    if (!activeRes) {
                        const log = logs.find(l => l.reservationId === r.id);
                        if (log) {
                            if (log.currentStatus === VEHICLE_MOVEMENT_EVENTS.LOADING_START) {
                                status = 'LOADING';
                                activeRes = r;
                            } else if (log.currentStatus === VEHICLE_MOVEMENT_EVENTS.DOCK_ARRIVAL) {
                                status = 'OCCUPIED';
                                activeRes = r;
                            }
                        }
                    }

                    // 3. Fallback: Removed.
                    // We DO NOT assume occupied just because time matches. 
                    // Vehicle must explicitly change status for dock to be occupied.
                    // This prevents delayed vehicles from appearing as "Docked".
                }
            }

            // Calculate Occupancy (Daily Density)
            const startHourStr = params?.operationStartHour || '08:00';
            const endHourStr = params?.operationEndHour || '18:00';
            const slotDuration = parseInt(params?.slotDuration || 30);

            // Parse times
            const [sH, sM] = startHourStr.split(':').map(Number);
            const [eH, eM] = endHourStr.split(':').map(Number);
            const totalOpMins = (eH * 60 + eM) - (sH * 60 + sM);

            // Calculate slots & Density
            const totalReservedMinutes = dockReservations.reduce((acc, r) => {
                const dur = dayjs(r.endTime).diff(dayjs(r.startTime), 'minute');
                return acc + (dur > 0 ? dur : 0);
            }, 0);

            // User Logic: (OccupiedSlots / TotalSlots) * 100
            // Which is mathematically equivalent to (TotalReservedMinutes / TotalOpMinutes) * 100
            const density = totalOpMins > 0 ? (totalReservedMinutes / totalOpMins) * 100 : 0;

            setOccupancyPercent(density);

            setDailyMetrics({
                totalVolume: vTotal, completedVolume: vComp,
                totalWeight: wTotal, completedWeight: wComp,
                totalVehicles: countTotal, completedVehicles: countComp, noShowVehicles: countNoShow, activeVehicles: countActive
            });


            if (dockData?.status !== 'maintenance') {
                setDockStatus(status);
            }
            setCurrentVehicle(activeRes);

            // ... (rest of helper) ...
        } catch (err) {
            console.error('DockStatusScreen load error:', err);
        } finally {
            setLoading(false);
        }
    }, [tenantId, dockId]);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 5000);
        return () => clearInterval(interval);
    }, [loadData]);

    if (loading) {
        return (
            <div className="dock-screen-loading">
                <div className="loader" />
                <p>Yükleniyor...</p>
            </div>
        );
    }

    if (!dock) {
        return (
            <div className="dock-screen-error">
                <h2>Rampa Bulunamadı</h2>
                <p>ID: {dockId}</p>
            </div>
        );
    }

    // List Logic
    const now = dayjs();

    // History: Finished or Missed Schedules
    const historyList = reservations.filter(r => {
        if (currentVehicle && r.id === currentVehicle.id) return false;
        // Completed -> History
        if (r.status === 'completed' || r.status === 'loading_completed') return true;
        // Pending but time fully passed (e.g. End Time passed) -> History (No Show)
        if (WAITING_STATUSES.includes(r.status) && now.isAfter(dayjs(r.endTime))) return true;
        return false;
    });

    // Upcoming: Pending (including delayed but not fully missed) or Active waiting
    const upcomingList = reservations.filter(r => {
        if (currentVehicle && r.id === currentVehicle.id) return false;
        // If in history, skip
        if (r.status === 'completed' || r.status === 'loading_completed') return false;
        if (WAITING_STATUSES.includes(r.status) && now.isAfter(dayjs(r.endTime))) return false;
        return true;
    });

    return (
        <div className="dock-screen-split" style={{ '--status-color': STATUS_COLORS[dockStatus] }}>
            {/* Status Bar - Top */}
            {/* Status Bar - Top */}
            <header className="dock-screen-status-bar" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem 2rem',
                minHeight: '100px'
            }}>
                {/* Left: Status */}
                <div>
                    <StatusIndicator status={dockStatus} />
                </div>

                {/* Right: Dock Name */}
                <div style={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap'
                }}>
                    <h1 style={{
                        margin: 0,
                        fontSize: '3rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        textShadow: '0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.4)',
                        fontFamily: '"Rajdhani", sans-serif'
                    }}>
                        {dock?.name || ''}
                    </h1>
                </div>
            </header>

            {/* Main Content - Split Layout */}
            <main className="dock-screen-main">
                {/* Top Row: Hero Section (Full Width) */}
                <div className="dashboard-hero-section">
                    <div className="hero-clock-block">
                        <DigitalClock />
                    </div>
                    <div className="hero-metrics-block">
                        <DockMetricsGrid occupancy={occupancyPercent} metrics={dailyMetrics} />
                    </div>
                </div>

                {/* Bottom Row: Split Content */}
                <div className="dock-content-split">
                    {/* Left Column: Current Vehicle */}
                    <section className="dock-panel-bottom-left">
                        <div className="panel-header-wrapper">
                            <h2 className="panel-title">
                                🚛 RAMPADAKİ ARAÇ
                            </h2>
                        </div>
                        <CurrentVehicleCard reservation={currentVehicle} />
                    </section>

                    {/* Right Column: Lists */}
                    <section className="dock-panel-bottom-right">
                        <div className="panel-header-wrapper">
                            <h2 className="panel-title">📅 GÜNLÜK ARAÇ LİSTESİ ({upcomingList.length})</h2>
                        </div>

                        {/* Colored Card Container */}
                        <div className="dock-right-card-content">
                            {/* UPCOMING / ACTIVE LIST */}
                            <div className="flex-1 flex flex-col min-h-0">
                                <div className="queue-list" style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                                    {upcomingList.length > 0 ? (
                                        upcomingList.map((res, idx) => (
                                            <QueueVehicleCard
                                                key={res.id}
                                                reservation={res}
                                                index={idx}
                                                isHistory={false}
                                            />
                                        ))
                                    ) : (
                                        <div className="empty-queue">
                                            <p>Sırada araç yok</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* HISTORY / COMPLETED LIST */}
                            <div className="flex-1 flex flex-col min-h-0" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px', marginTop: '16px' }}>
                                <h2 className="panel-title" style={{ marginBottom: '10px', fontSize: '0.9rem', opacity: 0.7 }}>✅ TAMAMLANAN / GEÇMİŞ ({historyList.length})</h2>
                                <div className="queue-list" style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                                    {historyList.length > 0 ? (
                                        historyList.map((res, idx) => (
                                            <QueueVehicleCard
                                                key={res.id}
                                                reservation={res}
                                                index={idx}
                                                isHistory={true}
                                            />
                                        ))
                                    ) : (
                                        <div className="empty-queue">
                                            <p>İşlem geçmişi yok</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default DockStatusScreen;
